import classNames from "classnames"
import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { LabeledCheckboxInput } from "web/components/generic/LabeledCheckbox"
import { LabeledTextInput } from "web/components/generic/LabeledTextInput"
import { TimeInterval } from 'web/engine/outline-engine'
import { useFocusOnMount } from 'web/hooks/useFocusOnMount'
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { useValidatedState } from "web/hooks/useValidatedState"
import { useEditFight_BadName } from 'web/redux/slices/edit-fight-slice'
import { ModalActions } from "web/redux/slices/modal-slice"
import { secondsToTimestamp, strictParseTimestamp, timestampToSeconds } from "web/utils/time-utils"
import { assertDefined, joinLines } from "web/utils/utils"
import { ModalContainerProps } from "./_ModalContainer"

export type EditSegmentModalProps = {
    segmentId: string
}

export const EditSegmentModal: React.FC<EditSegmentModalProps & ModalContainerProps> = (props) => {

    const disp = useDispatch()

    const [editFightSelectors, editFightActions] = useEditFight_BadName()
    const segment = editFightSelectors.getSegment(props.segmentId)

    const [name, setName] = useState(segment.name)
    const [condition, setCondition] = useState(segment.condition)
    const [adjustSegments, setAdjustSegments] = useState(true)
    const [dontMoveEvents, setDontMoveEvents] = useState(false)

    const timestampInputRef = useFocusOnMount()

    const validInterval = editFightSelectors.getValidSegmentInterval(
        segment,
        { includeMaxOffset: !adjustSegments }
    )

    const validatedTimestamp = useValidatedState({
        value: secondsToTimestamp(segment.offset),
        clearErrorOnChange: true,
        validator: (value) => {
            assertDefined(value)
            return timestampValidator(value, validInterval)
        }
    })

    const save = () => {
        if (!validatedTimestamp.validate()) {
            return
        }

        disp(editFightActions.updateSegment({
            segmentId: segment.id,
            name: name,
            offset: timestampToSeconds(validatedTimestamp.value),
            shiftSubsequentSegments: adjustSegments,
            condition: condition
        }))

        props.closeSelf()
    }

    const deleteSeg = (skipConfirm: boolean) => {
        if (segment.userCreated === false) {
            throw Error(`How even?`)
        }

        const doDelete = () => {
            props.closeSelf()
            disp(editFightActions.deleteSegment({ segmentId: segment.id }))
        }

        if (skipConfirm) {
            doDelete()
        } else {
            disp(ModalActions.openConfirmModal({
                title: "Delete segment?",
                btnColor: "danger",
                content: (
                    <>
                        <p>{"You sure?"}</p>
                        <p className="mb-0 small text-muted">
                            {"Tip: you can shift+click delete a segment to skip this confirmation."}
                        </p>
                    </>
                ),
                onConfirm: () => {
                    doDelete()
                }
            }))
        }
    }

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: save,
    })

    return (
        <Modal
            isOpen
            centered
            fade={false}
            backdrop={"static"}
            toggle={props.closeSelf}
        >
            <ModalHeader
            >
                {"Edit segment"}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">

                <div className="d-flex space-mx-3">
                    <LabeledTextInput
                        small
                        className="w-50"
                        label="Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onBlur={() => {
                            if (name === "") {
                                setName(segment.name)
                            }
                        }}
                    />

                    <LabeledTextInput
                        small
                        innerRef={timestampInputRef}
                        readOnly={segment.userCreated === false}
                        invalid={validatedTimestamp.validationError != null}
                        label="Timestamp"
                        value={validatedTimestamp.value}
                        onChange={e => validatedTimestamp.setValue(e.target.value)}
                        onBlur={() => {
                            try {
                                validatedTimestamp.setValue(strictParseTimestamp(validatedTimestamp.value))
                            } catch (error) {
                                validatedTimestamp.setValue(secondsToTimestamp(segment.offset))
                            }
                        }}
                    />

                    <LabeledTextInput
                        small
                        readOnly={segment.userCreated === false}
                        invalid={false}
                        label="Condition (optional)"
                        value={condition}
                        onChange={e => setCondition(e.target.value)}
                    />
                </div>

                {segment.userCreated &&
                    <div>
                        <LabeledCheckboxInput
                            small
                            className="mt-3"
                            label="Shift subsequent segments"
                            checked={adjustSegments}
                            onChange={e => setAdjustSegments(e)}
                        />
                        {/* <LabeledCheckboxInput
                            small
                            className="mt-3"
                            label="Don't move containing events (NYI)"
                            checked={dontMoveEvents}
                            onChange={e => setDontMoveEvents(e)}
                        /> */}
                    </div>
                }

                {segment.userCreated === false &&
                    <div className="mt-3 p-2 alert-warning border small">
                        {"The first segment cannot be moved"}
                    </div>
                }

                {validInterval != null &&
                    <div className="mt-3 p-2 alert-warning border small">

                        {validInterval.maxOffset != null
                            ? joinLines([
                                `Segment must be between ${secondsToTimestamp(validInterval.minOffset)}`,
                                `and ${secondsToTimestamp(validInterval.maxOffset)}`
                            ])
                            : `Segment cannot start earlier than ${secondsToTimestamp(validInterval.minOffset)}`
                        }

                    </div>
                }

            </ModalBody>

            <ModalFooter>
                <div className={classNames(
                    'd-flex w-100 align-items-end',
                    { 'justify-content-end': segment.userCreated === false },
                    { 'justify-content-between': segment.userCreated },
                )}>

                    {segment.userCreated &&
                        <Button
                            outline
                            size="sm"
                            onClick={e => deleteSeg(e.shiftKey)}
                            color="danger"
                        >
                            {"Delete segment"}
                        </Button>
                    }

                    <div className="d-flex space-mx-2">
                        <Button
                            color="success"
                            size="sm"
                            onClick={save}
                            title="Save changes (ctrl+s)"
                        >
                            {"Save changes"}
                        </Button>

                        <Button
                            outline
                            size="sm"
                            onClick={props.closeSelf}
                            title="Cancel edit (esc)"
                        >
                            {"Cancel edit"}
                        </Button>
                    </div>

                </div>
            </ModalFooter>
        </Modal >
    )
}

function timestampValidator(value: string, validInterval?: TimeInterval) {
    const offsetSeconds = timestampToSeconds(value)

    if (validInterval) {
        if (validInterval.maxOffset != null && validInterval.maxOffset < offsetSeconds) {
            return "Timestamp too late"
        }

        if (validInterval.minOffset > offsetSeconds) {
            return "Timestamp too early"
        }
    }
}
